import {
  createRouter,
  createWebHistory,
  createWebHashHistory,
} from "vue-router";
import HomeView from "@/views/HomeView.vue";
import AboutUsView from "@/views/AboutUsView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import DashboardView from "@/views/DashboardView.vue";
import LessonsView from "@/views/LessonsView.vue";
import FreeChatView from "@/views/FreeChatView.vue";
import AccountView from "@/views/AccountView.vue";
import MobileOnboardingView from "@/views/MobileOnboardingView.vue";
import PrivacyPolicyView from "@/views/PrivacyPolicyView.vue";
import DeleteAccountView from "@/views/DeleteAccountView.vue";
import TermsOfServiceView from "@/views/TermsOfServiceView.vue";
import BlogListView from "@/views/blog/BlogListView.vue";
import BlogPostView from "@/views/blog/BlogPostView.vue";
import FAQView from "@/views/FAQView.vue";
import { authStore } from "@/store/auth.js";
import { authUtils } from "@/scripts/authUtils.js";
import { Routes } from "@/constants/routes.js";
import {
  dailyPlayAllowed,
  isDailyPlayRoute,
  landingRoute,
} from "@/scripts/landingRoute.js";
import { pause as pauseSharedAudio } from "@/scripts/audioPlaybackService.js";
import i18n from "@/locales";
import { installDocumentTitle } from "@/scripts/pageTitle.js";

// @capacitor/core sets window.Capacitor on every platform (it ships a 'web' stub),
// so existence isn't a native-shell signal. isNativePlatform() returns false on web.
const isNativeShell = () =>
  typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.() === true;

const routes = [
  {
    path: "/",
    name: "Home",
    component: HomeView,
  },
  {
    // Legacy alias. Vue CLI still emits the shell as app.html, and Hosting's
    // cleanUrls 301s /app.html to /app; this finishes the chain so an old
    // shared link lands on the homepage rather than a second, meta-less copy
    // of it. EXACT path on purpose: /app/billing-return and
    // /app/billing-cancel below are Stripe checkout return URLs and must not
    // be swallowed by this redirect.
    path: "/app",
    redirect: "/",
  },
  // {
  //   path: "/chats",
  //   name: "Chats",
  //   component: ChatView,
  // },
  {
    // Deliberately not themed: the mobile onboarding slides keep their locked
    // light design (splash-adjacent artwork); dark mode starts at the dashboard.
    path: "/onboarding",
    name: "Onboarding",
    component: MobileOnboardingView,
  },
  {
    path: "/about-us",
    name: "About Us",
    component: AboutUsView,
  },
  {
    path: "/privacy-policy",
    name: "Privacy Policy",
    component: PrivacyPolicyView,
  },
  {
    path: "/terms-of-service",
    name: "Terms of Service",
    component: TermsOfServiceView,
  },
  {
    // Public account-deletion instructions — required by Google Play (and
    // referenced from App Store privacy answers). Must stay reachable without
    // sign-in and without the app installed.
    path: "/delete-account",
    name: "Delete Account",
    component: DeleteAccountView,
  },
  {
    path: "/blog",
    name: "Blog",
    component: BlogListView,
  },
  {
    path: "/blog/:slug",
    name: "Blog Post",
    component: BlogPostView,
  },
  {
    path: "/faq",
    name: "FAQ",
    component: FAQView,
  },
  {
    // Daily Play's public share page. A shared result link is
    // /play?s={shareId}, and the id is in the query string on purpose: the
    // build-time head injector writes one static file per registered path, so
    // every share link is served dist/play.html with Daily Play's own Open
    // Graph head. Public, because the people it is shared with have no account
    // yet; the page reads a record the database rules make world-readable by
    // exact id and calls no endpoint. Lazy, so the app shell does not carry it.
    path: "/play",
    name: "Daily Play Share",
    component: () => import("../views/DailyPlayShareView.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/admin",
    name: "Admin",
    component: () => import("../views/AdminView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/contacts",
    name: "Admin Contacts",
    component: () => import("../views/AdminContactsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/metrics",
    name: "Admin Metrics",
    component: () => import("../views/AdminMetricsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/latency",
    name: "Admin Latency",
    component: () => import("../views/AdminLatencyView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/content",
    name: "Admin Content",
    component: () => import("../views/AdminContentView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/stores",
    name: "Admin Stores",
    component: () => import("../views/AdminStoresView.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Stripe checkout lands here when the native app-link interception does
    // not fire; the page bounces the user back into the app.
    path: "/app/billing-return",
    name: "Billing Return",
    component: () => import("../views/BillingReturnView.vue"),
    meta: { billingOutcome: "return" },
  },
  {
    path: "/app/billing-cancel",
    name: "Billing Cancel",
    component: () => import("../views/BillingReturnView.vue"),
    meta: { billingOutcome: "cancel" },
  },
  {
    path: "/:catchAll(.*)*",
    name: "Not Found",
    meta: { titleKey: "errors.notFound.title" },
    component: NotFoundView,
  },
  {
    path: "/dashboard",
    component: DashboardView,
    meta: { requiresAuth: true, themed: true },
    children: [
      {
        // Daily Play's home, and the route landingRoute() sends a flagged
        // learner to. Lazy like every other destination that is not the two
        // original tabs: an account without the flag never loads the chunk.
        // titleKey is required rather than decorative -- src/scripts/
        // pageTitle.js falls back to the bare brand name for a route without
        // one, so the tab would read "ArticuLearn" on every open.
        path: "today",
        name: "Today",
        component: () => import("../views/TodayView.vue"),
        meta: { depth: 0, titleKey: "pageTitle.today" },
      },
      {
        // The round itself. Nested UNDER today on purpose: BottomTabBar decides
        // which tab is lit with `route.path.startsWith(tab.route)`, so a round
        // in progress keeps Today lit rather than lighting nothing.
        path: "today/play",
        name: "Daily Play",
        component: () => import("../views/DailyPlayView.vue"),
        // depth 1, one deeper than Today. DashboardView picks its transition
        // with `toDepth >= fromDepth ? "slide-left" : "slide-right"`, so an
        // equal depth made leaving a round animate as a forward slide, the
        // same direction as entering it. Every other nested destination in
        // this router is a depth deeper than the screen it opens from.
        meta: { depth: 1, titleKey: "pageTitle.today" },
      },
      {
        path: "course",
        name: "My Course",
        component: LessonsView,
        meta: { depth: 1, titleKey: "navigation.myCourse" },
      },
      {
        path: "chat",
        name: "My Chats",
        component: FreeChatView,
        meta: { depth: 0, titleKey: "navigation.myChats" },
      },
      {
        path: "account",
        name: "Account",
        component: AccountView,
        meta: { depth: 2, titleKey: "navigation.account" },
      },
      {
        // Reached from the Voice Ledger card on My Course, not from the tab
        // bar: it is a detail page for one card, not a fifth destination.
        // Lazy so the bundle only pays for it when a learner opens it.
        path: "progress",
        name: "My Progress",
        component: () => import("../views/ProgressView.vue"),
        meta: { depth: 3, titleKey: "navigation.myProgress" },
      },
      // {
      //   path: "",
      //   redirect: "/dashboard/lessons",
      // },
    ],
  },
];

const router = createRouter({
  history: isNativeShell() ? createWebHashHistory() : createWebHistory(),
  routes,
  // Without this, a push keeps the previous page's window scroll, so opening
  // e.g. /terms-of-service from a scrolled page lands mid-document. Restore
  // the saved position on back/forward, start at the top otherwise. Dashboard
  // surfaces scroll inner containers, not the window, so they are unaffected.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash };
    return { top: 0 };
  },
});

// The tab title follows the interface locale. `to.name` is an English route
// identifier ("My Progress") and used to be written straight to document.title,
// so every tab and history entry stayed English whatever the reader had chosen.
// Registered on afterEach and on a locale watcher rather than inside the guard
// below: that guard also runs every auth check, and a lazily-loaded bundle can
// land after it. Routes whose view sets its own useHead title are deliberately
// absent from the titleKey map -- see src/scripts/pageTitle.js.
installDocumentTitle(router, i18n);

router.beforeEach(async (to, from, next) => {
  const isCapacitor = isNativeShell();

  // On Capacitor, wait for auth to initialize once before any guard logic
  if (isCapacitor) {
    await authUtils.waitForAuthInitialization();
  }

  // Capacitor: guard /onboarding — authenticated users go to dashboard
  if (to.path === "/onboarding") {
    if (!isCapacitor) {
      next({ path: "/" });
      return;
    }
    if (authStore.getUserId()) {
      // Awaited: landingRoute() reads the dailyPlay flag for this uid, and the
      // flag store may not have reported yet. It is bounded at
      // LANDING_FLAG_WAIT_MS and falls back to My Chats, so the worst case is
      // a short pause rather than a guard that never calls next().
      next({ path: await landingRoute() });
      return;
    }
    next();
    return;
  }

  // Capacitor: redirect from landing page based on auth state
  if (to.path === "/" && isCapacitor) {
    next({
      path: authStore.getUserId() ? await landingRoute() : Routes.ONBOARDING,
    });
    return;
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!isCapacitor) {
      await authUtils.waitForAuthInitialization();
    }

    if (!authStore.getUserId()) {
      if (isCapacitor) {
        next({ path: Routes.ONBOARDING });
      } else {
        next({
          path: "/",
          query: { redirect: to.fullPath },
        });
      }
      return;
    }
  }

  // Daily Play's two routes are the only surfaces for this feature that a
  // learner can reach without going through a control the flag already gates.
  // BottomTabBar builds its Today entry inside a `featureFlags.dailyPlay`
  // branch, AppNavigation's item is `v-if`'d on the same flag, and
  // landingRoute() only answers Today when it is on -- but a typed URL, a
  // bookmark, or a native session restored onto this route reaches the view
  // regardless. `dailyPlayApi` then refuses the uid with 403 `featureOff`,
  // which TodayView can only render as "We could not load today's challenge."
  // over a Try again button that is refused identically every time.
  //
  // Fails CLOSED, on the same bounded wait landingRoute uses and for the same
  // reason: a learner bounced to My Chats because their flags had not arrived
  // yet sees the Today tab appear the moment they do, because the tab bar is
  // reactive, and reaches the round in one tap. The other way round -- letting
  // the route load when we have not heard -- is the dead error screen this
  // guard exists to prevent.
  if (isDailyPlayRoute(to.path) && authStore.getUserId()) {
    if (!(await dailyPlayAllowed())) {
      next({ path: Routes.DASHBOARD_CHAT });
      return;
    }
  }

  next();
});

router.afterEach((to) => {
  pauseSharedAudio();

  // Dark theme only renders on in-scope surfaces (spec §4); useTheme owns
  // the data-theme attribute AND the native status bar (its themedSurface
  // watcher re-syncs StatusBar style/background whenever the effective theme
  // changes, so no per-route StatusBar writes are needed here — this
  // supersedes cohesion PR12's per-route Style fix with the same Capacitor
  // semantics: Style.Light = dark icons on light bars). Dynamic import
  // keeps this module cycle-free. The currentRoute check drops stale
  // async resolutions when navigations land in quick succession (e.g. the
  // cold-start / -> dashboard redirect).
  import("@/composables/useTheme.js")
    .then(({ useTheme }) => {
      if (router.currentRoute.value.fullPath !== to.fullPath) return;
      useTheme().setThemedSurface(to.matched.some((record) => record.meta?.themed));
    })
    .catch(() => {
      // Chunk load failure: keep the current theme surface rather than crash.
    });
});

export default router;
